/**
 * Hooks for leaderboard scoring configuration and the recompute job
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { scoringApi } from '@api/endpoints/scoring'
import { leaderboardAdminApi } from '@api/endpoints/leaderboardAdmin'
import type { ScoringOptionsResponse, UpdateScoringOptionsRequest } from '@api/types/scoring'

const SCORING_OPTIONS_KEY = ['scoring-options']
const RECOMPUTE_STATUS_KEY = ['leaderboard-recompute-status']

// ===================
// QUERY HOOKS
// ===================

/**
 * Current scoring knobs, defaults, metadata and override provenance.
 * Not cached for long: an admin changing values wants to see them.
 */
export function useScoringOptions() {
  return useQuery({
    queryKey: SCORING_OPTIONS_KEY,
    queryFn: () => scoringApi.getOptions(),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  })
}

/**
 * Recompute status, polled only while a run is actually in flight.
 */
export function useRecomputeStatus() {
  return useQuery({
    queryKey: RECOMPUTE_STATUS_KEY,
    queryFn: () => leaderboardAdminApi.getRecomputeStatus(),
    refetchInterval: (query) => (query.state.data?.isRunning ? 3000 : false),
    refetchOnWindowFocus: true,
  })
}

// ===================
// MUTATION HOOKS
// ===================

export function useUpdateScoringOptions() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: UpdateScoringOptionsRequest) => scoringApi.updateOptions(request),
    onSuccess: (data: ScoringOptionsResponse) => {
      // The PUT returns the full fresh payload, so seed the cache rather than refetch.
      queryClient.setQueryData(SCORING_OPTIONS_KEY, data)
    },
  })
}

export function useResetScoringOptions() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => scoringApi.resetOptions(),
    onSuccess: (data: ScoringOptionsResponse) => {
      queryClient.setQueryData(SCORING_OPTIONS_KEY, data)
    },
  })
}

export function useTriggerRecompute() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => leaderboardAdminApi.triggerRecompute(),
    // The client default retries mutations once. For this POST that turned a 503 ("no worker is
    // listening") into a second request and a longer "Requesting…", and a retry that did land
    // would queue a second rebuild. A failed request is reported, never retried.
    retry: false,
    onSuccess: () => {
      // The worker publishes "running" asynchronously; refetch to start polling.
      queryClient.invalidateQueries({ queryKey: RECOMPUTE_STATUS_KEY })
    },
    onError: () => {
      // A 409 means the status we hold is stale (someone else started a run): refresh it.
      queryClient.invalidateQueries({ queryKey: RECOMPUTE_STATUS_KEY })
    },
  })
}
